import React, { useEffect, useCallback, useState, createContext, useContext } from 'react';
import { Keyboard, X } from 'lucide-react';

/**
 * KeyboardShortcuts - Global keyboard shortcut manager
 * Features:
 * - Global shortcut registration
 * - Context-aware shortcuts (only active in certain views)
 * - Help modal showing all shortcuts
 * - Ctrl/Cmd + key combinations
 */

// Context for keyboard shortcuts
const KeyboardShortcutsContext = createContext({
  shortcuts: [],
  registerShortcut: () => {},
  unregisterShortcut: () => {},
  showHelp: false,
  setShowHelp: () => {}
});

// Provider component
export function KeyboardShortcutsProvider({ children }) {
  const [shortcuts, setShortcuts] = useState([]);
  const [showHelp, setShowHelp] = useState(false);

  const registerShortcut = useCallback((shortcut) => {
    setShortcuts(prev => {
      // Avoid duplicates
      const exists = prev.find(s => s.key === shortcut.key && s.ctrl === shortcut.ctrl && s.shift === shortcut.shift);
      if (exists) return prev;
      return [...prev, shortcut];
    });
  }, []);

  const unregisterShortcut = useCallback((key, ctrl = false, shift = false) => {
    setShortcuts(prev => prev.filter(s => !(s.key === key && s.ctrl === ctrl && s.shift === shift)));
  }, []);

  // Global keyboard listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        // Allow Escape to work even in inputs
        if (e.key !== 'Escape') return;
      }

      // Show help with ?
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp(true);
        return;
      }

      // Find matching shortcut
      const matchingShortcut = shortcuts.find(s => {
        const keyMatch = s.key.toLowerCase() === e.key.toLowerCase();
        const ctrlMatch = s.ctrl ? (e.ctrlKey || e.metaKey) : (!e.ctrlKey && !e.metaKey);
        const shiftMatch = s.shift ? e.shiftKey : !e.shiftKey;
        return keyMatch && ctrlMatch && shiftMatch && !s.disabled;
      });

      if (matchingShortcut) {
        e.preventDefault();
        matchingShortcut.action(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);

  return (
    <KeyboardShortcutsContext.Provider value={{
      shortcuts,
      registerShortcut,
      unregisterShortcut,
      showHelp,
      setShowHelp
    }}>
      {children}
      {showHelp && <KeyboardShortcutsHelp />}
    </KeyboardShortcutsContext.Provider>
  );
}

// Hook to use keyboard shortcuts
export function useKeyboardShortcuts() {
  return useContext(KeyboardShortcutsContext);
}

// Hook to register a shortcut on mount and unregister on unmount
export function useShortcut({ key, ctrl = false, shift = false, action, label, category = 'Global', disabled = false }) {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();

  useEffect(() => {
    registerShortcut({ key, ctrl, shift, action, label, category, disabled });
    return () => unregisterShortcut(key, ctrl, shift);
  }, [key, ctrl, shift, action, label, category, disabled, registerShortcut, unregisterShortcut]);
}

// Help modal showing all shortcuts
function KeyboardShortcutsHelp() {
  const { shortcuts, setShowHelp } = useKeyboardShortcuts();

  // Group shortcuts by category
  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    const category = shortcut.category || 'Global';
    if (!acc[category]) acc[category] = [];
    acc[category].push(shortcut);
    return acc;
  }, {});

  const formatShortcut = (shortcut) => {
    const parts = [];
    if (shortcut.ctrl) parts.push('Ctrl');
    if (shortcut.shift) parts.push('Shift');
    parts.push(shortcut.key.toUpperCase());
    return parts.join(' + ');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Keyboard className="w-6 h-6 text-amber-500" />
            <h2 className="text-xl font-bold text-gray-900">Raccourcis clavier</h2>
          </div>
          <button
            onClick={() => setShowHelp(false)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category} className="mb-6 last:mb-0">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between py-2 ${
                      shortcut.disabled ? 'opacity-50' : ''
                    }`}
                  >
                    <span className="text-gray-700">{shortcut.label}</span>
                    <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-mono text-gray-600">
                      {formatShortcut(shortcut)}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {Object.keys(groupedShortcuts).length === 0 && (
            <p className="text-gray-500 text-center py-8">
              Aucun raccourci disponible
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-center">
          <span className="text-sm text-gray-500">
            Appuyez sur <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs font-mono">?</kbd> pour afficher cette aide
          </span>
        </div>
      </div>
    </div>
  );
}

// Shortcut indicator component (to show next to buttons)
export function ShortcutIndicator({ shortcut, className = '' }) {
  return (
    <kbd className={`px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-500 ${className}`}>
      {shortcut}
    </kbd>
  );
}

// Default global shortcuts
export const DEFAULT_SHORTCUTS = [
  { key: 'k', ctrl: true, label: 'Recherche globale', category: 'Navigation' },
  { key: 'h', ctrl: true, label: 'Accueil', category: 'Navigation' },
  { key: 'n', ctrl: true, label: 'Nouveau', category: 'Actions' },
  { key: 's', ctrl: true, label: 'Sauvegarder', category: 'Actions' },
  { key: 'Escape', ctrl: false, label: 'Fermer modal', category: 'Global' }
];

export default KeyboardShortcutsProvider;
